import { useState, useEffect, useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import StatusPill from "./StatusPill";
import { FIELD_KEY, STATUS_LABELS, NEXT_STATUS, COLS, colStyle, cellInputStyle, cellSelectStyle } from "./sheetConstants";
import { fmtDate, toDateInput } from "./sheetHelpers";

// ── Sub-components ────────────────────────────────────────────────────────────

function DragHandle({ listeners, attributes }) {
  return (
    <span
      {...listeners}
      {...attributes}
      style={{ cursor: "grab", color: "#cbd5e1", fontSize: 13, userSelect: "none", display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}
      title="Drag to reorder"
    >
      ⠿
    </span>
  );
}

function PriorityBadge({ priority }) {
  const cfg = {
    High:   { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
    Medium: { bg: "#fffbeb", color: "#d97706", border: "#fde68a" },
    Low:    { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  };
  const c = cfg[priority] || { bg: "var(--surface-2)", color: "var(--text-3)", border: "var(--border)" };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 5, background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {priority || "—"}
    </span>
  );
}

// ── TaskRow ───────────────────────────────────────────────────────────────────

export default function TaskRow({ task: initialTask, indent, canManage, users, onSave, onSaved, isDragOverlay = false, onNavigate }) {
  const [task, setTask] = useState(initialTask);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef(null);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: initialTask.id, disabled: !canManage || !!editing });

  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 };

  // Only sync from parent when not mid-save — prevents parent re-renders from
  // overwriting optimistic state while an API call is in flight (T13)
  useEffect(() => { if (!saving) setTask(initialTask); }, [initialTask, saving]);
  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.focus(), 30); }, [editing]);

  function startEdit(field, value) {
    if (!canManage || saving || isDragOverlay) return;
    setSaveError("");
    setEditing(field); setDraft(value ?? "");
  }
  function cancelEdit() { setEditing(null); setDraft(""); }

  async function commitEdit(field, value) {
    setEditing(null); setDraft("");
    if (String(value ?? "") === String(task[FIELD_KEY[field] || field] ?? "")) return;
    if (field === "due" && value && task.startDate && value < task.startDate.split("T")[0]) {
      setSaveError("Due date cannot be before start date");
      return;
    }
    await save(field, value);
  }

  async function save(field, value) {
    setSaving(true);
    setSaveError("");
    const key = FIELD_KEY[field] || field;
    const patch = { [key]: value };
    if (field === "assignee") {
      const u = users.find(u => u.id === value);
      patch.assignedToUserName = u?.name || task.assignedToUserName;
    }
    setTask(t => ({ ...t, ...patch }));
    try {
      await onSave(task.id, field, value, { ...task, ...patch });
      onSaved?.();
    } catch (err) {
      setTask(initialTask);
      setSaveError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function toggleAtRisk() {
    if (!canManage) return;
    await save("atRisk", !task.atRisk);
  }

  function handleKeyDown(e, field, value) {
    if (e.key === "Enter")  { e.preventDefault(); commitEdit(field, value); }
    if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  }

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== "Completed";

  const rowBg = isDragOverlay
    ? "var(--surface-2)"
    : hovered && !editing
      ? "rgba(99,102,241,0.03)"
      : "var(--surface, white)";

  return (
    <div
      ref={setNodeRef}
      data-testid="task-row"
      style={{ ...style, display: "flex", alignItems: "center", padding: "0 12px", height: 40, borderBottom: "1px solid var(--border)", background: rowBg, boxShadow: isDragOverlay ? "0 6px 24px rgba(99,102,241,0.18)" : "none", opacity: saving ? 0.65 : (isDragging ? 0.35 : 1), transition: "background 0.1s" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Drag handle */}
      <div style={{ ...colStyle(COLS[0]), opacity: canManage && hovered ? 1 : 0, transition: "opacity 0.12s", height: "100%", display: "flex", alignItems: "center" }}>
        {canManage && <DragHandle listeners={listeners} attributes={attributes} />}
      </div>

      {/* ⚑ At Risk */}
      <div style={{ ...colStyle(COLS[1]), cursor: canManage ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={toggleAtRisk}
        title={canManage ? (task.atRisk ? "Remove at-risk flag" : "Mark as at risk") : ""}
      >
        <span style={{ fontSize: 12, opacity: task.atRisk ? 1 : (hovered && canManage ? 0.3 : 0.12), color: "#dc2626", transition: "opacity 0.15s" }}>⚑</span>
      </div>

      {/* Task name */}
      <div style={{ ...colStyle(COLS[2]), paddingLeft: indent ? 22 : 6, display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
        {indent && <span style={{ width: 2, height: 16, borderRadius: 2, background: "var(--phase-border)", flexShrink: 0 }} />}
        {editing === "title" ? (
          <input ref={inputRef} style={{ ...cellInputStyle, flex: 1 }} value={draft} maxLength={200}
            onChange={e => setDraft(e.target.value)}
            onBlur={() => commitEdit("title", draft)}
            onKeyDown={e => handleKeyDown(e, "title", draft)}
          />
        ) : (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{ display: "block", fontSize: 13, fontWeight: 500, color: task.status === "Completed" ? "var(--text-3)" : "var(--text)", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", cursor: canManage ? "text" : "default", textDecoration: task.status === "Completed" ? "line-through" : "none" }}
                onClick={() => canManage && startEdit("title", task.title)}
                title={task.title}
              >
                {task.title}
              </span>
              {task.brief && (
                <span style={{ display: "block", fontSize: 11, color: "var(--text-3)", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", marginTop: 1 }}>
                  {task.brief}
                </span>
              )}
            </div>
            {hovered && (
              <span
                style={{ fontSize: 10, color: "#6366f1", cursor: "pointer", flexShrink: 0, padding: "2px 5px", borderRadius: 4, background: "#eef2ff", fontWeight: 600 }}
                onClick={e => { e.stopPropagation(); onNavigate?.(`/tasks/${task.id}`); }}
                title="Open full detail"
              >↗</span>
            )}
          </>
        )}
      </div>

      {/* Assignee */}
      <div style={{ ...colStyle(COLS[3]), display: "flex", alignItems: "center", overflow: "hidden" }}>
        {editing === "assignee" ? (
          <select ref={inputRef} style={cellSelectStyle} value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={() => commitEdit("assignee", draft)}
            onKeyDown={e => handleKeyDown(e, "assignee", draft)}
          >
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 5, cursor: canManage ? "pointer" : "default" }}
            onClick={() => startEdit("assignee", task.assignedToUserId)}>
            {task.assignedToUserName ? (
              <>
                <span style={{ width: 20, height: 20, borderRadius: "50%", background: "#e0e7ff", color: "#6366f1", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {task.assignedToUserName.charAt(0).toUpperCase()}
                </span>
                <span style={{ fontSize: 12, color: "var(--text-2)", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{task.assignedToUserName.split(" ")[0]}</span>
              </>
            ) : (
              <span style={{ fontSize: 12, color: "var(--text-3)" }}>—</span>
            )}
          </div>
        )}
      </div>

      {/* Due */}
      <div style={{ ...colStyle(COLS[4]), display: "flex", alignItems: "center" }}>
        {editing === "due" ? (
          <input ref={inputRef} type="date" style={cellInputStyle} value={draft}
            min={new Date().toLocaleDateString('en-CA')}
            onChange={e => setDraft(e.target.value)}
            onBlur={() => commitEdit("due", draft || null)}
            onKeyDown={e => handleKeyDown(e, "due", draft || null)}
          />
        ) : (
          <span style={{ fontSize: 12, color: isOverdue ? "#dc2626" : "var(--text-2)", fontWeight: isOverdue ? 600 : 400, cursor: canManage ? "pointer" : "default", display: "flex", alignItems: "center", gap: 3 }}
            onClick={() => startEdit("due", toDateInput(task.dueDate))}>
            {isOverdue && <span>⚠</span>}
            {fmtDate(task.dueDate)}
          </span>
        )}
      </div>

      {/* Priority */}
      <div style={{ ...colStyle(COLS[5]), display: "flex", alignItems: "center" }}>
        {editing === "priority" ? (
          <select ref={inputRef} style={cellSelectStyle} value={draft}
            onChange={e => commitEdit("priority", e.target.value)}
            onBlur={cancelEdit}
            onKeyDown={e => { if (e.key === "Escape") cancelEdit(); }}
          >
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        ) : (
          <span style={{ cursor: canManage ? "pointer" : "default" }}
            onClick={() => startEdit("priority", task.priority)}>
            <PriorityBadge priority={task.priority} />
          </span>
        )}
      </div>

      {/* Status */}
      <div style={{ ...colStyle(COLS[6]), display: "flex", alignItems: "center", flexDirection: "column", justifyContent: "center", gap: 2 }}>
        {editing === "status" ? (
          <select ref={inputRef} style={cellSelectStyle} value={draft}
            onChange={e => commitEdit("status", e.target.value)}
            onBlur={cancelEdit}
            onKeyDown={e => { if (e.key === "Escape") cancelEdit(); }}
          >
            <option value={task.status}>{STATUS_LABELS[task.status] || task.status}</option>
            {(NEXT_STATUS[task.status] || []).map(v => (
              <option key={v} value={v}>{STATUS_LABELS[v] || v}</option>
            ))}
          </select>
        ) : (
          <span style={{ cursor: canManage ? "pointer" : "default" }}
            onClick={() => startEdit("status", task.status)}
            title={canManage ? "Click to change status" : ""}>
            <StatusPill status={task.status} />
          </span>
        )}
        {saveError && (
          <div style={{ fontSize: 10, color: "#dc2626", maxWidth: 140, whiteSpace: "normal", lineHeight: 1.3, textAlign: "center" }}>
            {saveError}
          </div>
        )}
      </div>
    </div>
  );
}
